import { useScreenHelp } from "./ScreenHelp";
// src/CqcEvidenceDialog.js
// Per-(site, quality statement, evidence category) dialog: where does the
// evidence for this live? Mirrors the ALT_SOURCES / attestation dialog in
// RegulatoryCompliance.js, extended so a source can carry an actual pointer
// (a link, or an uploaded file) rather than just a label.
//
// "Document" is its own source kind (folding in what used to be separate
// spreadsheet/word/file options — they were all "a document, either linked
// or uploaded" in practice) because a document specifically needs: a
// storage mode (an external link vs. an actual upload this app stores), a
// review date, and — new in this update — the ability to hand the whole
// job of setting it to a contributor by email, via the "Ask a contributor"
// panel at the bottom. See CqcContributorTask.js for the page that link
// opens.

import React, { useEffect, useState } from "react";
import { cqcApi } from "./cqcApi";
import { serviceGuidance } from "./cqcProfileModel";

const SOURCE_KINDS = [
  { kind: "existing-system", label: "Held externally (record location)", needsLink: true },
  { kind: "document", label: "Document (policy, SOP, certificate...)", isDocument: true },
  { kind: "database", label: "Database / other system", needsLink: true, placeholder: "Link or connection reference" },
  { kind: "manual", label: "Manual statement", needsLink: false, needsStatement: true },
  { kind: "other", label: "Other", needsLink: true, placeholder: "Describe where this evidence lives" },
];

// 8MB — mirrors backend/cqc_store.js's MAX_DOCUMENT_BYTES. Checked here too
// so a contributor gets an immediate, friendly error instead of waiting for
// a round-trip to the server to find out their file is too big. If you
// change one, change the other. Exported so CqcContributorTask.js's own
// upload form (same rules, different page) doesn't need its own copy.
export const MAX_UPLOAD_BYTES = 8 * 1024 * 1024;

export function fmtBytes(n) {
  if (n == null) return "";
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(0)} KB`;
  return `${(n / 1024 / 1024).toFixed(1)} MB`;
}

// FileReader -> raw base64 (strips the "data:...;base64," prefix that
// readAsDataURL includes).
export function readFileAsBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => reject(new Error("Couldn't read that file"));
    reader.onload = () => {
      const result = String(reader.result || "");
      const comma = result.indexOf(",");
      resolve(comma >= 0 ? result.slice(comma + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}

const S = {
  overlay: { position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", display: "flex",
             alignItems: "center", justifyContent: "center", zIndex: 1000, overflow: "auto", padding: "24px 0" },
  dialog: { background: "#fff", borderRadius: 10, width: 520, maxWidth: "92vw", padding: 20,
            boxShadow: "0 12px 40px rgba(0,0,0,0.25)", fontFamily: "system-ui, sans-serif" },
  title: { fontSize: 15, fontWeight: 700, margin: "0 0 2px" },
  sub: { fontSize: 12.5, color: "#5D5D5D", margin: "0 0 14px" },
  label: { fontSize: 12, fontWeight: 600, color: "#434343", margin: "10px 0 4px", display: "block" },
  select: { width: "100%", padding: "8px 10px", fontSize: 13, border: "1px solid #D9D9D9", borderRadius: 6 },
  input: { width: "100%", boxSizing: "border-box", padding: "8px 10px", fontSize: 13,
           border: "1px solid #D9D9D9", borderRadius: 6, fontFamily: "inherit" },
  textarea: { width: "100%", boxSizing: "border-box", padding: "8px 10px", fontSize: 13, minHeight: 70,
              border: "1px solid #D9D9D9", borderRadius: 6, fontFamily: "inherit", resize: "vertical" },
  row: { display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 18 },
  btn: { padding: "8px 14px", fontSize: 13, fontWeight: 600, border: "none", borderRadius: 6,
         background: "#0E8CFF", color: "#fff", cursor: "pointer" },
  btnSecondary: { padding: "8px 14px", fontSize: 13, fontWeight: 600, borderRadius: 6, cursor: "pointer",
                  background: "#fff", color: "#434343", border: "1px solid #D9D9D9" },
  err: { color: "#A8071A", fontSize: 12.5, marginTop: 8 },
  existing: { fontSize: 12, color: "#434343", background: "#FAFAFA", border: "1px solid #EDF2F7",
              borderRadius: 6, padding: "8px 10px", marginTop: 10 },
  existingMeta: { fontSize: 11, color: "#5D5D5D", marginTop: 3 },
  link: { color: "#0E8CFF", fontWeight: 600, textDecoration: "none" },
  toggleModeRow: { display: "flex", gap: 6, margin: "4px 0 2px" },
  modeBtn: (active) => ({ flex: 1, padding: "7px 10px", fontSize: 12, fontWeight: 600, borderRadius: 6, cursor: "pointer",
                           border: active ? "1px solid #262626" : "1px solid #D9D9D9",
                           background: active ? "#262626" : "#fff", color: active ? "#fff" : "#434343" }),
  fileRow: { display: "flex", alignItems: "center", gap: 8, marginTop: 4 },
  fileName: { fontSize: 12, color: "#434343", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" },
  hint: { fontSize: 11, color: "#8C8C8C", marginTop: 4 },
  divider: { borderTop: "1px solid #F0F0F0", margin: "16px 0 12px" },
  sendToggle: { fontSize: 12.5, fontWeight: 600, color: "#434343", background: "none", border: "none",
                cursor: "pointer", padding: 0, display: "flex", alignItems: "center", gap: 6 },
  sendPanel: { marginTop: 10, padding: 12, background: "#FAFAFA", border: "1px solid #F0F0F0", borderRadius: 8 },
  sendOk: { fontSize: 12, color: "#389E0D", background: "#F6FFED", border: "1px solid #B7EB8F",
            borderRadius: 6, padding: "6px 9px", marginTop: 8 },
};

export default function CqcEvidenceDialog({ site, groupId, itemId, element, existing, onClose, onSaved }) {
  const initialKind = (existing && existing.documentRef && existing.documentRef.kind) || (existing?.source === "Manual statement" ? "manual" : "existing-system");
  const initialIsDoc = initialKind === "document";
  const initialRef = (existing && existing.documentRef) || {};

  const [kind, setKind] = useState(initialIsDoc ? "document" : (["spreadsheet", "word", "file"].includes(initialKind) ? "document" : initialKind));
  const [label, setLabel] = useState(initialRef.label || "");
  const [link, setLink] = useState(initialRef.url || "");
  const [statement, setStatement] = useState((existing && existing.statement) || "");

  // Document-kind specific state.
  const [storageMode, setStorageMode] = useState(initialRef.storageMode || "link");
  const [docLink, setDocLink] = useState(initialRef.storageMode === "link" ? (initialRef.url || "") : "");
  const [reviewDate, setReviewDate] = useState(initialRef.reviewDate ? new Date(initialRef.reviewDate).toISOString().slice(0, 10) : "");
  const [pendingFile, setPendingFile] = useState(null); // { filename, contentType, fileBase64, bytes }
  const [fileError, setFileError] = useState(null);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  // "Ask a contributor" panel.
  const [sendOpen, setSendOpen] = useState(false);
  const [contributors, setContributors] = useState([]);
  const [contributorId, setContributorId] = useState("");
  const [background, setBackground] = useState("");
  const [sending, setSending] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [sendError, setSendError] = useState(null);

  useScreenHelp({ title: sendOpen ? "Request care evidence by email" : `Configure evidence — ${element.label || element.id}`, blurb: sendOpen ? "Send an evidence request for this item to the selected contributor." : `Configure the evidence source for ${site.name}. The saved source should explain where the supporting information is held.`, can: sendOpen ? ["Choose the contributor and check their email address.", "Review the request before sending it by email."] : ["Read the guidance, then select the appropriate source type.", "Complete the source details and review date where requested.", "Save the source so it is recorded against this item."], related: ["care-source", "care-email"] }, 80);
  const guidance = serviceGuidance(site, groupId, itemId, element.id);

  useEffect(() => {
    if (!sendOpen || contributors.length) return;
    cqcApi.listContributors(site._id).then((res) => setContributors(res.contributors || [])).catch(() => {});
    if (!background) {
      setBackground(`${guidance.purpose}\n\n${guidance.categoryHelp}${guidance.proofExamples && guidance.proofExamples.length ? "\n\nExamples of acceptable proof: " + guidance.proofExamples.join("; ") + "." : ""}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sendOpen]);

  const kindMeta = SOURCE_KINDS.find((k) => k.kind === kind) || SOURCE_KINDS[0];
  const existingDocRef = existing && existing.documentRef && existing.documentRef.kind === "document" ? existing.documentRef : null;
  const existingReviewPassed = existingDocRef && existingDocRef.reviewDate && new Date(existingDocRef.reviewDate).getTime() < Date.now();

  async function handleFileSelect(e) {
    setFileError(null);
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (file.size > MAX_UPLOAD_BYTES) {
      setFileError(`"${file.name}" is ${fmtBytes(file.size)} — the limit is ${fmtBytes(MAX_UPLOAD_BYTES)}. Try compressing it, or link to it instead (SharePoint, Google Drive...) rather than uploading.`);
      e.target.value = "";
      return;
    }
    try {
      const fileBase64 = await readFileAsBase64(file);
      setPendingFile({ filename: file.name, contentType: file.type || "application/octet-stream", fileBase64, bytes: file.size });
    } catch (err) {
      setFileError(err.message);
    }
  }

  function buildDocumentRef() {
    if (storageMode === "upload") {
      if (pendingFile) {
        return { kind: "document", storageMode: "upload", filename: pendingFile.filename,
                  contentType: pendingFile.contentType, fileBase64: pendingFile.fileBase64,
                  reviewDate: reviewDate || null };
      }
      // Nothing new selected — keep the existing uploaded file (only the
      // review date may have changed). cqc.js's storeDocumentAndBuildRef
      // recognises documentId-with-no-fileBase64 as "re-validate this
      // pointer, don't re-store" rather than re-uploading the same bytes.
      if (existingDocRef && existingDocRef.storageMode === "upload" && existingDocRef.documentId) {
        return { kind: "document", storageMode: "upload", documentId: existingDocRef.documentId, reviewDate: reviewDate || null };
      }
      throw new Error("Choose a file to upload");
    }
    if (!docLink.trim()) throw new Error("Enter a link to the document");
    return { kind: "document", storageMode: "link", url: docLink.trim(), label: label || null, reviewDate: reviewDate || null };
  }

  async function save() {
    setSaving(true); setError(null);
    try {
      if (kindMeta.needsLink && !link.trim() && !label.trim()) throw new Error("Enter a link or describe where the evidence is held");
      if (kindMeta.needsStatement && !statement.trim()) throw new Error("Enter the evidence statement");
      let documentRef = null;
      let sourceLabel;
      if (kind === "document") {
        documentRef = buildDocumentRef();
        sourceLabel = storageMode === "upload" ? "Uploaded document" : "Linked document";
      } else {
        sourceLabel = kindMeta.label;
        documentRef = kindMeta.needsLink ? { kind, label: label || sourceLabel, url: link } : null;
      }
      await cqcApi.saveAttestation({
        siteId: site._id, groupId, itemId, elementId: element.id,
        source: sourceLabel,
        statement: kindMeta.needsStatement ? statement : null,
        documentRef,
      });
      if (onSaved) await onSaved();
      onClose();
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  async function sendToContributor() {
    if (!contributorId) { setSendError("Choose a contributor first"); return; }
    setSending(true); setSendError(null); setSendResult(null);
    try {
      const res = await cqcApi.sendContributorTask({
        siteId: site._id, groupId, itemId, elementId: element.id, contributorId,
        background, itemLabel: guidance.itemLabel, elementLabel: element.label,
      });
      setSendResult(res);
    } catch (e) {
      setSendError(e.message);
    } finally {
      setSending(false);
    }
  }

  return (
    <div style={S.overlay} onClick={onClose}>
      <div style={S.dialog} onClick={(e) => e.stopPropagation()}>
        <div style={S.title}>{element.label}</div>
        <div style={S.sub}>{site.name} — source for {guidance.itemLabel} · {element.label}</div>

        {existing && (
          <div style={S.existing}>
            Currently recorded: <strong>{existing.source || "—"}</strong>
            {existingDocRef && existingDocRef.storageMode === "link" && existingDocRef.url && (
              <> — <a style={S.link} href={existingDocRef.url} target="_blank" rel="noreferrer">open link</a></>
            )}
            {existingDocRef && existingDocRef.storageMode === "upload" && existingDocRef.documentId && (
              <>
                {" — "}
                <a style={S.link} href={cqcApi.documentUrl(existingDocRef.documentId)} target="_blank" rel="noreferrer">view</a>
                {" · "}
                <a style={S.link} href={cqcApi.documentUrl(existingDocRef.documentId, true)}>download</a>
                {existingDocRef.filename ? ` (${existingDocRef.filename}${existingDocRef.bytes ? ", " + fmtBytes(existingDocRef.bytes) : ""})` : ""}
              </>
            )}
            {!existingDocRef && existing.documentRef && existing.documentRef.url ? ` — ${existing.documentRef.url}` : ""}
            <div style={S.existingMeta}>
              {existing.recordedBy ? `by ${existing.recordedBy}` : ""}
              {existing.recordedAt ? ` on ${new Date(existing.recordedAt).toLocaleDateString()}` : ""}
              {existingDocRef && existingDocRef.reviewDate && (
                <span style={existingReviewPassed ? { color: "#A8071A", fontWeight: 600 } : undefined}>
                  {" · "}review date {new Date(existingDocRef.reviewDate).toLocaleDateString()}
                  {existingReviewPassed ? " (passed — this now shows as overdue)" : ""}
                </span>
              )}
            </div>
          </div>
        )}

        <label style={S.label}>Where does this evidence live?</label>
        <select style={S.select} value={kind} onChange={(e) => setKind(e.target.value)}>
          {SOURCE_KINDS.map((k) => <option key={k.kind} value={k.kind}>{k.label}</option>)}
        </select>

        {kind === "document" && (
          <>
            <div style={S.toggleModeRow}>
              <button type="button" style={S.modeBtn(storageMode === "link")} onClick={() => setStorageMode("link")}>Link to it</button>
              <button type="button" style={S.modeBtn(storageMode === "upload")} onClick={() => setStorageMode("upload")}>Upload it here</button>
            </div>

            {storageMode === "link" ? (
              <>
                <label style={S.label}>Link</label>
                <input style={S.input} value={docLink} onChange={(e) => setDocLink(e.target.value)}
                       placeholder="Link to the document (SharePoint, Google Drive, network path...)" />
              </>
            ) : (
              <>
                <label style={S.label}>File</label>
                <div style={S.fileRow}>
                  <input type="file" onChange={handleFileSelect} />
                </div>
                {pendingFile && (
                  <div style={S.fileName}>Selected: {pendingFile.filename} ({fmtBytes(pendingFile.bytes)})</div>
                )}
                {!pendingFile && existingDocRef && existingDocRef.storageMode === "upload" && (
                  <div style={S.fileName}>Keeping the current file: {existingDocRef.filename}. Choose a new one to replace it.</div>
                )}
                {fileError && <div style={S.err}>{fileError}</div>}
                <div style={S.hint}>Up to {fmtBytes(MAX_UPLOAD_BYTES)}. Larger files: link to it instead.</div>
              </>
            )}

            <label style={S.label}>Review date</label>
            <input type="date" style={S.input} value={reviewDate} onChange={(e) => setReviewDate(e.target.value)} />
            <div style={S.hint}>Once this date passes, this item shows as overdue again even though evidence is on file — insurance certificates and time-bound policies need re-checking.</div>
          </>
        )}

        {kindMeta.needsLink && (
          <>
            <label style={S.label}>Label (optional)</label>
            <input style={S.input} value={label} onChange={(e) => setLabel(e.target.value)}
                   placeholder="Short name for this source" />
            <label style={S.label}>Link / reference</label>
            <input style={S.input} value={link} onChange={(e) => setLink(e.target.value)}
                   placeholder={kindMeta.placeholder} />
          </>
        )}

        {kindMeta.needsStatement && (
          <>
            <label style={S.label}>Statement</label>
            <textarea style={S.textarea} value={statement} onChange={(e) => setStatement(e.target.value)}
                      placeholder="Describe how this site meets this evidence category..." />
          </>
        )}

        {error && <div style={S.err}>{error}</div>}

        <div style={S.row}>
          <button style={S.btnSecondary} onClick={onClose} disabled={saving}>Cancel</button>
          <button style={S.btn} onClick={save} disabled={saving}>{saving ? "Saving…" : "Save"}</button>
        </div>

        <div style={S.divider} />
        <button type="button" style={S.sendToggle} onClick={() => setSendOpen((v) => !v)}>
          {sendOpen ? "▾" : "▸"} Ask a contributor to provide this instead
        </button>
        {sendOpen && (
          <div style={S.sendPanel}>
            <div style={{ fontSize: 11.5, color: "#5D5D5D", marginBottom: 8 }}>
              Emails a link the contributor can open with no login — they choose a link or upload a
              file themselves, add a comment, and see messages here. Works for {" "}
              {"the configured access period"}.
            </div>
            <label style={S.label}>Contributor</label>
            <select style={S.select} value={contributorId} onChange={(e) => setContributorId(e.target.value)}>
              <option value="">Choose…</option>
              {contributors.filter((c) => c.active !== false).map((c) => (
                <option key={c._id} value={c._id}>{c.name}{c.role ? ` — ${c.role}` : ""} ({c.email})</option>
              ))}
            </select>
            <label style={S.label}>Background — what's needed, and why</label>
            <textarea style={S.textarea} value={background} onChange={(e) => setBackground(e.target.value)} />
            {sendError && <div style={S.err}>{sendError}</div>}
            {sendResult && <div style={S.sendOk}>Sent to {sendResult.sentTo}. Link expires {new Date(sendResult.expiresAt).toLocaleDateString()}.</div>}
            <div style={S.row}>
              <button style={S.btn} onClick={sendToContributor} disabled={sending}>{sending ? "Sending…" : "Send email"}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
